import { createSelector } from 'reselect';
import { Location } from 'react-router-dom';
import { FILTER_JACKPOT, ROUTE_LIVE_CASINO } from '~/components/router/constants';
import { EnhancedGame } from '~/components/casino-shared/types';
import { InitialState } from '.';

const REDUCER_NAME = 'allGames';

// [REDUCER_NAME]? , a question mark here is for search component
// if casino, or liveCasino, or allGames undefined in the store to not crash app
// some of them can be empty because we use explore page with categories, and without
// games without categories store in allGames reducer

export type AllGamesState = {
	allGames: InitialState;
};

const gamesByGameCodeSelector = (state: AllGamesState) => state[REDUCER_NAME]?.games;
const hydratedSelector = (state: AllGamesState) => state[REDUCER_NAME]?.hydrated;
const loadingSelector = (state: AllGamesState) => state[REDUCER_NAME]?.loading;
const gameNamesByIdxSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameNamesByIdx;
const gameCodesByProviderCodeSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameCodesByProviderCode;
const gameCodesBySubProviderCodeSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameCodesBySubProviderCode;
const providersSelector = (state: AllGamesState) => state[REDUCER_NAME]?.providers;
const subProvidersSelector = (state: AllGamesState) => state[REDUCER_NAME]?.subProviders;
const gameCodesByIdxSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameCodesByIdx;
const gameIdxByFilterSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameIdxByFilter;
const gameIdxByProviderSelector = (state: AllGamesState) => state[REDUCER_NAME]?.gameIdxByProvider;

const gamesByIndexSelector = createSelector(
	[gamesByGameCodeSelector, gameCodesByIdxSelector],
	(gamesByGameCode, gameCodes) => {
		if (!gameCodes) return {};
		return gameCodes.reduce<Record<number, EnhancedGame>>((acc, gameCode, idx) => {
			acc[idx] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

const gamesSelector = createSelector([gamesByGameCodeSelector], gamesByGameCode => {
	if (!gamesByGameCode) return [];
	return Object.values(gamesByGameCode);
});

const gamesByNameSelector = createSelector(
	[gameNamesByIdxSelector, gameCodesByIdxSelector, gamesByGameCodeSelector],
	(gameNames, gameCodes, gamesByGameCode) => {
		if (!gameCodes) return {};
		return gameCodes.reduce<Record<string, EnhancedGame>>((acc, gameCode, idx) => {
			acc[gameNames[idx]] = gamesByGameCode[gameCode];
			return acc;
		}, {});
	}
);

export const FILTER_MAPPINGS = {
	[FILTER_JACKPOT]: 'jackpot'
};

// TODO Should these selector be here please check.
export const filterSelector = (_: unknown, props: { filter: string }) => props.filter;
export const isLiveCasinoLocationSelector = (_: unknown, props: { location: Location }) =>
	props.location.pathname.includes(ROUTE_LIVE_CASINO);

export const filteredGamesSelector = createSelector(
	[
		gamesByGameCodeSelector,
		gameCodesByIdxSelector,
		gameIdxByFilterSelector,
		hydratedSelector,
		filterSelector,
		isLiveCasinoLocationSelector
	],
	(gamesByGameCode, gameCodesByIdx, gameIdxByFilter, hydrated, filter, isLiveCasinoLocation) => {
		let filteredGames: EnhancedGame[] = [];
		if (!hydrated) return filteredGames;
		if (!filter) {
			filteredGames = Object.values(gamesByGameCode);
			if (isLiveCasinoLocation) {
				filteredGames = filteredGames.filter(game => game.categoryCode === 'CASINO_LIVE_CASINO');
			}
			return filteredGames;
		}
		const activeFilter = FILTER_MAPPINGS[filter as keyof typeof FILTER_MAPPINGS];
		if (!activeFilter || !gameIdxByFilter[activeFilter as keyof typeof gameIdxByFilter]) {
			return [];
		}
		filteredGames =
			gameIdxByFilter[activeFilter as keyof typeof gameIdxByFilter]?.map(
				gameIdx => gamesByGameCode[gameCodesByIdx[gameIdx]]
			) ?? [];

		if (isLiveCasinoLocation) {
			filteredGames = filteredGames.filter(game => game.categoryCode === 'CASINO_LIVE_CASINO');
		}

		return filteredGames;
	}
);

export {
	gamesSelector,
	gamesByIndexSelector,
	gamesByGameCodeSelector,
	gameIdxByFilterSelector,
	gameCodesByIdxSelector,
	hydratedSelector,
	gameNamesByIdxSelector,
	gameIdxByProviderSelector,
	gamesByNameSelector,
	loadingSelector,
	gameCodesByProviderCodeSelector,
	gameCodesBySubProviderCodeSelector,
	providersSelector,
	subProvidersSelector
};
